/**
 * Suspense boundary for lazily-mounted app packages.
 *
 * Shared so every app mount falls back the same way, and so the fallback stays
 * inside the shell chrome (rail, topbar) rather than blanking the whole page —
 * a route chunk arriving is not a navigation, and it should not look like one.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { NotFoundProvider } from '@mono/ui-runtime/shell';
import { Suspense } from 'react';
import { NotFoundPage } from '../pages/NotFound/NotFoundPage';
import { PageBody } from './layout';
/**
 * Supplying the not-found page here — rather than letting the app throw and
 * relying on the root errorElement — is what keeps a mistyped in-app URL from
 * writing an uncaught error to the console. See @mono/ui-runtime's notFound.
 *
 * One consequence to be aware of: the 404 now renders inside the shell chrome,
 * where a throw to the root errorElement replaced the whole screen. The rail
 * and topbar stay put and the user keeps their navigation, which is the better
 * of the two, but it is a visible difference from before the route split.
 */ export function AppSuspense({ children }) {
    return /*#__PURE__*/ _jsx(NotFoundProvider, {
        render: ()=>/*#__PURE__*/ _jsx(NotFoundPage, {}),
        children: /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(PageBody, {
                children: /*#__PURE__*/ _jsxs("output", {
                    className: "flex min-h-[40vh] w-full items-center justify-center",
                    "aria-live": "polite",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "sr-only",
                            children: "Loading"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-foreground"
                        })
                    ]
                })
            }),
            children: children
        })
    });
}
